import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { AreaInfo, InsertAreaType } from "IF/interfaceType";
import axios from "axios";
import { API_BASE_URL, RESPONSE_CODE } from "Utils/apiUtil";
import { UserRole, useSessionStorage } from "IF/userSession";
import { MessageModal } from "./MessageModal";
import DeleteIcon from "@mui/icons-material/Delete";
import ColorPickerDialog from "./ColorPickerForm";
import ConfirmationModal from "./ConfirmationModal";
import SettingAreaMobile from "./SettingAreaMobile";
import { COLORS } from "../theme/colors";
import { DataContext } from "Contexts/DataProvider";
import { createTitle } from "Utils/format";
export const SettingArea = () => {
  // ユーザ情報
  const { getUserData } = useSessionStorage();
  const userData = getUserData();
  const { id: userId, companyCode, userRole } = userData;

  // サイドバーデータ持ち回り
  const sidebarContext = useContext(DataContext);
  if (!sidebarContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { menuItems, setChoiceMenu, toggleMenu } = sidebarContext;

  const insertInitData: InsertAreaType = {
    serialNo: "",
    name: "エリア名",
    sortBy: 0,
    colorCode: "",
    userId: userId,
  };

  // 各列の幅割合
  const columnWidths: Record<string, string> = {
    simId: "25%",
    serialNo: "25%",
    magId: "10%",
    name: "25%",
    sortBy: "5%",
    color: "5%",
    action: "5%",
  };

  // エリア一覧
  const [data, setData] = useState<AreaInfo[]>([]);
  // 初期エリア一覧データ
  const [originalData, setOriginalData] = useState<AreaInfo[]>([]);
  // 登録用
  const [newArea, setNewArea] = useState<InsertAreaType>(insertInitData);

  // レイアウト設定
  const [tableBodyHeight, setTableBodyHeight] = useState(600);
  const [cardBodyWidth, setCardBodyWidth] = useState(0);
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const [homeTitle, setHomeTitle] = useState("");
  const [alertTitle, setAlertTitle] = useState("");

  // モーダル
  const [isModalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const closeModal = () => {
    setModalOpen(false);
  };
  // 新規登録モーダル開閉
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setNewArea(insertInitData);
  };

  // 確認モーダル
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState<React.ReactNode>("");
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);
  const handleConfirmModalClose = () => setConfirmModalOpen(false);

  const handleConfirmModalOpen = (
    message: React.ReactNode,
    confirmAction: () => void
  ) => {
    setConfirmMessage(message);
    setOnConfirm(() => confirmAction);
    setConfirmModalOpen(true);
  };

  // カラー選択ダイアログ
  const [dialogColorOpen, setDialogColorOpen] = useState<boolean>(false);
  const [currentRowSimId, setCurrentRowSimId] = useState<string | null>(null);

  const handleColorSelect = (color: { name: string; hex: string }) => {
    if (currentRowSimId) {
      setData((prevData) =>
        prevData.map((item) =>
          item.simId === currentRowSimId
            ? { ...item, colorCode: color.hex, updateUser: userId }
            : item
        )
      );
      setCurrentRowSimId(null);
    }
  };

  const openColorPicker = (simId: string) => {
    setCurrentRowSimId(simId);
    setDialogColorOpen(true);
  };

  const closeColorPicker = () => {
    setDialogColorOpen(false);
    setCurrentRowSimId(null);
  };

  // タイトル設定
  useEffect(() => {
    if (menuItems.length > 2) {
      const index = UserRole.YKC !== userRole ? 3 : 4;
      document.title = createTitle(menuItems[index]?.subItems?.[1]?.text ?? "");
      setChoiceMenu(menuItems[index]?.subItems?.[1]?.text ?? "");
      toggleMenu("設定");
      setHomeTitle(menuItems[index - 3].text);
      setAlertTitle(menuItems[index - 2].text);
    }
  }, [userRole, menuItems, setChoiceMenu, toggleMenu]);

  // 初期表示時処理
  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight - 150;
      const windowWidth = window.innerWidth - 43;
      setTableBodyHeight(windowHeight);
      setCardBodyWidth(windowWidth);
    };

    // 初期表示時とリサイズ時に高さを計算
    handleResize();
    window.addEventListener("resize", handleResize);

    // 初期表示データ取得
    getData(companyCode);

    // クリーンアップ
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [companyCode]);

  // エリア情報取得処理
  const getData = async (schema: string) => {
    try {
      // API呼び出し
      const response = await axios.get(`${API_BASE_URL}/AreaSetting/GetAreas`, {
        params: { schema: schema },
      });
      setData(response.data);
      setOriginalData(response.data);
    } catch (error) {
      setModalOpen(true);
      setMessage("エリア情報の取得に失敗しました");
      console.error("エリア情報の取得に失敗しました:", error);
    }
  };

  // text入力変更処理
  const handleTextChange = (
    id: number,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target as
      | HTMLInputElement
      | HTMLSelectElement;
    setData((prevData) =>
      prevData?.map((data) =>
        data.id === id ? { ...data, [name]: value, updateUser: userId } : data
      )
    );
  };

  // 表示順選択変更処理
  const handleSortChange = (id: number, value: number) => {
    setData((prevData) =>
      prevData?.map((data) =>
        data.id === id ? { ...data, sortBy: value, updateUser: userId } : data
      )
    );
  };

  // 初期データと変更後formを比較
  const getModifiedElements = () => {
    if (originalData && data) {
      return data.filter((current, index) => {
        const original: AreaInfo = originalData[index];
        return (
          current.serialNo !== original?.serialNo ||
          current.name !== original?.name ||
          current.sortBy !== original.sortBy ||
          current.colorCode !== original.colorCode
          // current.use !== original.use
        );
      });
    } else {
      return [];
    }
  };

  // 新規登録処理
  const handleAddArea = async () => {
    try {
      // 未使用のカラーコード
      const usedColorCodes = data.map((area) => area.colorCode);
      const availableColors = COLORS.filter(
        (colorOption) => !usedColorCodes.includes(colorOption.hex)
      );
      const selectedColor =
        availableColors.length > 0 ? availableColors[0].hex : COLORS[0].hex;

      // API呼び出し
      const response = await axios.post(
        `${API_BASE_URL}/AreaSetting/PostArea`,
        {
          userData: { ...userData, companyCode: userData.companyCode },
          ...newArea,
          sortBy: data.filter((d) => d.use).length + 1,
          colorCode: selectedColor,
        }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("エリアが登録されました。");
        handleClose();
        getData(companyCode);
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("エリアの登録に失敗しました。");
        console.error("エリアの登録に失敗しました。", error);
      }
    }
  };

  // 更新時処理
  const handleUpdate = async () => {
    const modifiedElements = getModifiedElements();
    // 変更なしの場合更新しない
    if (modifiedElements.length === 0) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    // ユーザIDを設定
    modifiedElements.map((modifiedItem) => ({
      ...modifiedItem,
      updateUser: userId,
    }));
    try {
      const response = await axios.put(`${API_BASE_URL}/AreaSetting/PutArea`, {
        schema: companyCode,
        AreaDataList: modifiedElements,
      });
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("エリア情報が更新されました。");
        getData(companyCode);
      } else {
        throw new Error();
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("エリア情報の更新に失敗しました。");
        console.error("エリアの更新に失敗しました。", error);
      }
    }
  };

  // 削除処理
  const handleDelete = async (index: number) => {
    const deleteData = data[index];
    try {
      // API呼び出し
      const response = await axios.delete(
        `${API_BASE_URL}/AreaSetting/DeleteArea`,
        {
          headers: { "Content-Type": "application/json" },
          data: {
            userData: userData,
            areaData: { ...deleteData, updateUser: userId },
          },
        }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("エリア情報が削除されました。");
        getData(companyCode);
      } else {
        throw new Error();
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("エリア情報の削除に失敗しました。");
        console.error("エリアの削除に失敗しました。", error);
      }
    }
  };

  return (
    <>
      <ColorPickerDialog
        open={dialogColorOpen}
        onClose={closeColorPicker}
        onSelect={handleColorSelect}
        initialColor={
          currentRowSimId
            ? data.find((item) => item.simId === currentRowSimId)?.colorCode ||
              "#FFFFFF"
            : "#FFFFFF"
        }
      />
      <MessageModal
        open={isModalOpen}
        message={message}
        handleClose={closeModal}
      />
      <ConfirmationModal
        open={confirmModalOpen}
        message={confirmMessage}
        handleClose={handleConfirmModalClose}
        onConfirm={onConfirm!}
      />
      {/* 新規登録モーダル */}
      <Dialog open={open} onClose={handleClose}>
        <Box sx={{ backgroundColor: theme.palette.custom.sForm }}>
          <DialogTitle>エリア登録</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="シリアルNo"
              fullWidth
              variant="outlined"
              value={newArea.serialNo}
              inputProps={{ maxLength: 6 }}
              onChange={(e) =>
                setNewArea({
                  ...newArea,
                  serialNo: e.target.value,
                })
              }
            />
            <TextField
              autoFocus
              margin="dense"
              label="エリア名"
              fullWidth
              variant="outlined"
              value={newArea.name}
              onChange={(e) =>
                setNewArea({
                  ...newArea,
                  name: e.target.value,
                })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>キャンセル</Button>
            <Button onClick={handleAddArea} variant="contained">
              登録
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Box>
        {isMd ? (
          // スマホ・タブレット
          <>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Button
                variant="contained"
                onClick={handleOpen}
                sx={{ width: "50%" }}
              >
                新規登録
              </Button>
              <Button
                variant="contained"
                onClick={handleUpdate}
                sx={{ width: "50%", backgroundColor: "#000080" }}
              >
                更新
              </Button>
            </Box>
            <Typography
              fontWeight="fontWeightMedium"
              sx={{ fontSize: "0.875rem", position: "relative", top: "-8px" }}
            >
              ※同じエリア名は{homeTitle}／{alertTitle}
              画面でスイッチをエリア名称に切り替えることで、情報を合わせて表示できます。
            </Typography>
            <Box
              sx={{
                minHeight: tableBodyHeight - 40,
                maxHeight: tableBodyHeight - 40,
                overflowY: "auto",
              }}
            >
              <Grid2 container spacing={2}>
                {data.map((row, index) => (
                  <Grid2 size={{ xs: 12, sm: 6 }} key={row.id}>
                    <SettingAreaMobile
                      key={row.id}
                      row={row}
                      originalRow={originalData[index]}
                      index={index}
                      width={cardBodyWidth}
                      size={data.filter((d) => d.use).length}
                      userRole={userRole}
                      handleDelete={handleDelete}
                      handleTextChange={handleTextChange}
                      handleSortChange={handleSortChange}
                      handleConfirmModalOpen={handleConfirmModalOpen}
                      openColorPicker={openColorPicker}
                    />
                  </Grid2>
                ))}
              </Grid2>
            </Box>
          </>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                minHeight: tableBodyHeight,
                maxHeight: tableBodyHeight,
                overflowY: "auto",
                backgroundColor: theme.palette.custom.row,
              }}
            >
              <Table
                sx={{
                  "& .MuiTable-root": {
                    fontSize: "0.8rem",
                  },
                }}
                size={theme.breakpoints.down("xs") ? "small" : "medium"}
              >
                <TableHead
                  sx={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: theme.palette.custom.tableHeader,
                    zIndex: 1,
                    color: "white",
                  }}
                >
                  <TableRow>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        verticalAlign: "middle",
                        width: columnWidths.simId,
                        color: "white",
                      }}
                    >
                      <Typography fontWeight="fontWeightBold">IMSI</Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        verticalAlign: "middle",
                        width: columnWidths.serialNo,
                        color: "white",
                      }}
                    >
                      <Typography fontWeight="fontWeightBold">
                        シリアルNo
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        verticalAlign: "middle",
                        width: columnWidths.magId,
                        color: "white",
                      }}
                    >
                      <Typography fontWeight="fontWeightBold">
                        磁界ID
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        verticalAlign: "middle",
                        width: columnWidths.name,
                        color: "white",
                      }}
                    >
                      <Typography fontWeight="fontWeightBold">
                        エリア名
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        verticalAlign: "middle",
                        width: columnWidths.sortBy,
                        color: "white",
                      }}
                    >
                      <Typography fontWeight="fontWeightBold">
                        表示順
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        verticalAlign: "middle",
                        width: columnWidths.color,
                        color: "white",
                      }}
                    >
                      <Typography fontWeight="fontWeightBold">
                        色選択
                      </Typography>
                    </TableCell>
                    {(userRole === UserRole.YKC ||
                      userRole === UserRole.RENTAL) && (
                      <TableCell
                        sx={{
                          textAlign: "center",
                          width: columnWidths.action,
                          color: "white",
                        }}
                      >
                        <Typography fontWeight="fontWeightBold">
                          削除
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.map((area, index) => (
                    <TableRow
                      key={area.id}
                      sx={{
                        bgcolor: area.use
                          ? theme.palette.custom.row
                          : "#707070",
                      }}
                    >
                      <TableCell
                        sx={{
                          textAlign: "center",
                          verticalAlign: "middle",
                          width: columnWidths.simId,
                        }}
                      >
                        {area.simId}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          verticalAlign: "middle",
                          width: columnWidths.serialNo,
                          minWidth: "120px",
                        }}
                      >
                        {userRole === UserRole.YKC ||
                        userRole === UserRole.RENTAL ? (
                          <TextField
                            value={area.serialNo}
                            name="serialNo"
                            inputProps={{ maxLength: 6, fontSize: "0.8rem" }}
                            fullWidth
                            disabled={!area.use}
                            onChange={(e) => handleTextChange(area.id, e)}
                          />
                        ) : (
                          area.serialNo
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          verticalAlign: "middle",
                          width: columnWidths.magId,
                        }}
                      >
                        {area.magId}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          verticalAlign: "middle",
                          width: columnWidths.name,
                          minWidth: "180px",
                        }}
                      >
                        <TextField
                          value={area.name}
                          name="name"
                          inputProps={{ maxLength: 16, fontSize: "0.8rem" }}
                          fullWidth
                          disabled={!area.use}
                          onChange={(e) => handleTextChange(area.id, e)}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          verticalAlign: "middle",
                          width: columnWidths.sortBy,
                        }}
                      >
                        {area.use ? (
                          <Select
                            name="sortBy"
                            value={area.sortBy}
                            onChange={(e) =>
                              handleSortChange(
                                area.id,
                                parseInt(e.target.value as string, 10)
                              )
                            }
                            label="表示順"
                          >
                            {Array.from(
                              { length: data.filter((d) => d.use).length },
                              (_, i) => (
                                <MenuItem key={i + 1} value={i + 1}>
                                  {i + 1}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        ) : (
                          "ー"
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          verticalAlign: "middle",
                          width: columnWidths.colorCode,
                        }}
                      >
                        <IconButton
                          disabled={!area.use}
                          onClick={() => openColorPicker(area.simId)}
                        >
                          <Box
                            sx={{
                              width: "24px",
                              height: "24px",
                              backgroundColor: area.colorCode,
                              border: "1px solid #000",
                              borderRadius: "4px",
                            }}
                          />
                        </IconButton>
                      </TableCell>
                      {/* <TableCell
                        sx={{
                          textAlign: "center",
                          verticalAlign: "middle",
                          width: columnWidths.colorCode,
                        }}
                      >
                        {userRole === UserRole.YKC ||
                        userRole === UserRole.RENTAL ? (
                          <Checkbox
                            checked={area.use}
                            name="use"
                            onChange={(event, checked) =>
                              handleCheckboxChange(area.simId, event, checked)
                            }
                          />
                        ) : (
                          <Checkbox checked={area.use} disabled />
                        )}
                      </TableCell> */}
                      {userRole === UserRole.YKC ||
                      userRole === UserRole.RENTAL ? (
                        <TableCell
                          sx={{
                            textAlign: "center",
                            verticalAlign: "middle",
                            width: columnWidths.action,
                          }}
                        >
                          {area.use ? (
                            <Tooltip title={"削除"} placement="top" arrow>
                              <IconButton
                                onClick={() =>
                                  handleConfirmModalOpen(
                                    <Box>
                                      選択したエリアを削除します。よろしいですか？
                                      <br />
                                      <span style={{ color: "red" }}>
                                        ※削除したエリアは編集ができなくなります。
                                      </span>
                                    </Box>,
                                    () => handleDelete(index)
                                  )
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            "ー"
                          )}
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box
              sx={{
                marginTop: 1.5,
                display: "flex",
                justifyContent: "space-between", // 左端と右端に配置
                alignItems: "center", // 垂直方向の中央揃え
              }}
            >
              <Typography
                fontWeight="fontWeightMedium"
                sx={{ fontSize: "0.875rem", position: "relative", top: "-8px" }}
              >
                ※同じエリア名は{homeTitle}／{alertTitle}
                画面でスイッチをエリア名称に切り替えることで、情報を合わせて表示できます。
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleOpen}
                  sx={{
                    width: {
                      xs: "100%",
                      sm: "100%",
                      md: "20vh",
                    },
                  }}
                >
                  新規登録
                </Button>
                <Button
                  variant="contained"
                  style={{ backgroundColor: "#000080" }}
                  sx={{
                    width: {
                      xs: "100%",
                      sm: "100%",
                      md: "20vh",
                    },
                  }}
                  onClick={handleUpdate}
                >
                  更新
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </>
  );
};
export default SettingArea;
